import React from 'react';
import {createPortal} from 'react-dom';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { createPopper } from '@popperjs/core';
import { connect } from 'react-redux';

import AppSlx from 'app/selectors/app';

class Pop extends React.PureComponent {
  constructor(props) {
    super(props);
    this.ref = React.createRef();
  }
  componentDidMount() {
    this.props.onMount(this.element);
  }
  get element() {
    return this.ref.current;
  }
  get hasArrow() {
    return ['tooltip', 'popover'].includes(this.props.popType);
  }
  render() {
    const {isActive, popType, className} = this.props;
    return (
      <div className={`popper-pop popper-${popType} ${isActive ? 'active' : 'inactive'} ${className}`} ref={this.ref}>
        {this.props.children}
        {this.hasArrow && (
          <div className="popper-pop-arrow" data-popper-arrow />
        )}
      </div>
    );
  }
}
Pop.propTypes = {
  onMount: PropTypes.func.isRequired,
  isActive: PropTypes.bool,
  popType: PropTypes.oneOf(['popover', 'tooltip', 'menu']),
  className: PropTypes.string,
};
Pop.defaultProps = {
  isActive: false,
  popType: 'tooltip',
  className: '',
};

class Popper extends React.PureComponent {

  constructor(props) {
    super(props);

    this.state = {
      isHovered: false,
      isClicked: false,
      isFocused: false,
    };

    this.wasActive = false;

    this.onTargetMouseEnter = this.onTargetMouseEnter.bind(this);
    this.onTargetMouseLeave = this.onTargetMouseLeave.bind(this);
    this.onTargetFocus = this.onTargetFocus.bind(this);
    this.onTargetBlur = this.onTargetBlur.bind(this);
    this.onTargetClick = this.onTargetClick.bind(this);
    this.onPopMount = this.onPopMount.bind(this);
    this.onClickDocument = this.onClickDocument.bind(this);

    this.refTarget = React.createRef();
    this.refPop = React.createRef();

    this.hoverTimeout = null;
    this.blurTimeout = null;
  }

  componentDidMount() {
    document.addEventListener('click', this.onClickDocument);
  }

  componentWillUnmount() {
    clearTimeout(this.hoverTimeout);
    clearTimeout(this.blurTimeout);
    document.removeEventListener('click', this.onClickDocument);
  }

  componentDidUpdate(prevProps) {
    const {isActive, wasActive} = this;
    this.wasActive = this.isActive;
    const didClose = !!(!isActive && wasActive);
    const didOpen  = !!(isActive && !wasActive);
    if (didClose) {
      this.props.onClose && this.props.onClose();
    }
    if (didOpen) {
      this.props.onOpen && this.props.onOpen();
    }
  }

  onPopMount(elPop) {
    const elTarget = this.refTarget.current;
    const {placement, edgeDistance} = this.props;
    const modifiers = [
      {name: 'arrow', options: {padding: 12}},
      {name: 'offset', options: {offset: [0, this.offsetDistance]}},      
    ];
    const hasEdgeDistance = edgeDistance !== -1;
    if (hasEdgeDistance) {
      modifiers.push({name: 'preventOverflow', options: {padding: edgeDistance}});
    }
    this._popper = createPopper(elTarget, elPop, {placement, modifiers});
  }

  get isActive() {
    const {isActive, hoverTrigger, focusTrigger, clickTrigger} = this.props;
    const {isHovered, isClicked, isFocused} = this.state;
    if (_.isBoolean(isActive)) return isActive;
    if (hoverTrigger && isHovered) return true;
    if (focusTrigger && isFocused) return true;
    if (clickTrigger && isClicked) return true;
    return false;
  }

  get offsetDistance() {
    if (_.isFinite(this.props.offsetDistance)) return this.props.offsetDistance;
    if (this.props.popType === 'menu') return 2;
    return 12;
  }

  get elPop() {
    return this.refPop.current?.element;
  }

  // use this instead of onBlur or else onBlur will hide the popper before clicks within the popper can register
  onClickDocument(event) {
    const newState = {};
    const isTarget = this.refTarget.current.contains(event.target);
    const isPopper = this.refPop?.current?.element?.contains(event.target);
    // poppers use portals so can't really detect if they're outside of event.target's tree; best to assume clicking in another popper should not dismiss this one
    const isAnotherPopper = !!event.target.closest('.popper-menu');
    const isModal = !!event.target.closest('.modal');
    const isOutside = !isTarget && !isPopper && !isAnotherPopper && !isModal;
    if (!isTarget) {
      newState.isFocused = false;
    }
    if (isOutside) {
      newState.isClicked = false;
    }
    if (Object.keys(newState).length) {
      this.setState(newState);
    }
  }

  onTargetMouseEnter(...args) {
    if (!this.props.disabled && this.props.hoverTrigger) {
      clearTimeout(this.hoverTimeout);
      this.hoverTimeout = setTimeout(() => {
        this.setState({isHovered: true});
      }, this.props.hoverDelay);
    }
    this.props.onMouseEnter && this.props.onMouseEnter(...args);
  }
  onTargetMouseLeave(...args) {
    if (this.props.hoverTrigger) {
      clearTimeout(this.hoverTimeout);
      this.setState({isHovered: false});
    }
    this.props.onMouseLeave && this.props.onMouseLeave(...args);
  }
  onTargetFocus(...args) {
    if (!this.props.disabled && this.props.focusTrigger) {
      this.setState({isFocused: true});
    }
    this.props.onFocus && this.props.onFocus(...args);
  }
  onTargetBlur(...args) {
    if (!this.props.disabled && this.props.blurUntrigger) {
      this.setState({isFocused: false});
    }
    this.props.onBlur && this.props.onBlur(...args);
  }
  onTargetClick(...args) {
    if (!this.props.disabled && this.props.clickTrigger) {
      this.setState((prevState) => {
        return {...prevState, isClicked: !prevState.isClicked};
      });
    }
    this.props.onClick && this.props.onClick(...args);
    setTimeout(() => {
      Popper.updateAll();
    }, 0);
  }

  renderPop() {
    const {usePortal, isServer, popType, popContent, popClassName} = this.props;
    const content = _.isFunction(popContent) ? (this.isActive ? popContent() : null) : popContent;
    const pop = <Pop popType={popType} isActive={this.isActive} onMount={this.onPopMount} className={popClassName} ref={this.refPop}>{content}</Pop>;
    if (isServer) return null;
    if (!usePortal) return pop;
    return createPortal(pop, document.getElementById('poppers'));
  }

  render() {
    const {tagType, className, children} = this.props;
    const passProps = _.omit(this.props, ['isActive', 'hoverTrigger', 'tagType', 'className', 'hoverTrigger', 'focusTrigger', 'clickTrigger', 'hoverDelay', 'placement', 'edgeDistance', 'offsetDistance', 'popType', 'popContent', 'isServer', 'dispatch', 'usePortal', 'blurUntrigger', 'popClassName', 'clickOutsideUntrigger', 'onClose', 'onOpen']);
    const Target = tagType;
    return (<>
      <Target
        {...passProps}
        className={`popper-target ${className}`}
        ref={this.refTarget}
        onMouseEnter={this.onTargetMouseEnter}
        onMouseLeave={this.onTargetMouseLeave}
        onFocus={this.onTargetFocus}
        onBlur={this.onTargetBlur}
        onClick={this.onTargetClick}
      >
        {children}
      </Target>
      {this.renderPop()}
    </>);
  }

}

Popper.propTypes = {
  popType: PropTypes.oneOf(['tooltip', 'popover', 'menu']),
  popContent: PropTypes.oneOfType([PropTypes.node, PropTypes.func]).isRequired,
  popClassName: PropTypes.string,
  tagType: PropTypes.oneOf(['div', 'span', 'a', 'button', 'th', 'td', 'label']),
  className: PropTypes.string,
  isActive: PropTypes.bool,
  hoverTrigger: PropTypes.bool,
  focusTrigger: PropTypes.bool,
  clickTrigger: PropTypes.bool,
  placement: PropTypes.oneOf(['top', 'top-start', 'top-end', 'right', 'bottom', 'bottom-end', 'left', 'bottom-start']), // TODO: there are more of these
  hoverDelay: PropTypes.number,
  disabled: PropTypes.bool,
  usePortal: PropTypes.bool,
  blurUntrigger: PropTypes.bool, // when true, the blur event on target will hide the popper
  clickOutsideUntrigger: PropTypes.bool, // when true, clicking outside the poppper will hide popper
  edgeDistance: PropTypes.number,
  offsetDistance: PropTypes.number,
  onClose: PropTypes.func,
  onOpen: PropTypes.func,
};

Popper.defaultProps = {
  popType: 'tooltip',
  popClassName: '',
  tagType: 'div',
  className: '',
  isActive: null,
  hoverTrigger: false,
  focusTrigger: false,
  clickTrigger: false,
  placement: 'top',
  hoverDelay: 0,
  disabled: false,
  usePortal: false,
  blurUntrigger: false,
  clickOutsideUntrigger: false,
  edgeDistance: -1,
  offsetDistance: null,
  onClose: null,
  onOpen: null,
};

/**
 *  Trigger layout calculation for poppers, no delay
 */
Popper.updateAllNow = () => {
  if (__IS_SERVER__) return;
  // this will cause the poppers to recalculate position
  // called upon render because elements being added/removed can change the position of a popper target without the popper instance instance knowing
  window.dispatchEvent(new Event('scroll'));
};

/**
 *  Trigger layout calculation for poppers, debounced
 */
Popper.updateAll = _.debounce(Popper.updateAllNow, 200);

const stateToProps = (state) => ({
  isServer: AppSlx.isServer(state),
});

export default connect(stateToProps, undefined, undefined, {forwardRef: true})(Popper);
